import React, {useCallback, useEffect, useState} from 'react';
import {Stack, Typography} from '@mui/material';
import {useNavigate} from 'react-router';
import TagList from '../../../components/documentContent/TagList';
import {
    addEntityTag,
    listEntityTags,
    removeEntityTag,
    suggestSharedTags,
} from '../../../firebase/firestore/sharedTags';
import SharedSuggestedTags from './SharedSuggestedTags';

/**
 * Shared free-form tags for ICR / EEI / GAP / REPORT / CT / CM.
 * Reuses the segment TagList look (+ autocomplete, colored #chips)
 * and LLM Suggested tags strip when canEdit (stacked full-width below chips).
 */
export default function EntityTagChips({
    entityType,
    entityId,
    canEdit = false,
    enabled = true,
    dense = false, // kept for call-site compat; TagList is inherently compact
    showSuggestions = true,
}) {
    const navigate = useNavigate();
    const [tags, setTags] = useState([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [loadingOps, setLoadingOps] = useState({});

    const reload = useCallback(async () => {
        if (!enabled || !entityType || !entityId) {
            setTags([]);
            return;
        }
        try {
            const rows = await listEntityTags(entityType, entityId);
            setTags((rows || []).map((r) => ({name: r.tag})).filter((t) => t.name));
            setError(null);
        } catch (e) {
            setError(e.message);
            setTags([]);
        }
    }, [enabled, entityType, entityId]);

    useEffect(() => {
        reload();
    }, [reload]);

    const createTag = useCallback(async (name) => {
        if (!canEdit || !name?.trim()) return;
        setBusy(true);
        try {
            await addEntityTag(entityType, entityId, name.trim());
            await reload();
        } catch (e) {
            setError(e.message);
            throw e;
        } finally {
            setBusy(false);
        }
    }, [canEdit, entityType, entityId, reload]);

    const removeTag = useCallback(async (name) => {
        if (!canEdit || !name) return;
        setLoadingOps((p) => ({...p, [name]: true}));
        try {
            await removeEntityTag(entityType, entityId, name);
            await reload();
        } catch (e) {
            setError(e.message);
        } finally {
            setLoadingOps((p) => {
                const next = {...p};
                delete next[name];
                return next;
            });
        }
    }, [canEdit, entityType, entityId, reload]);

    const searchTagsFn = useCallback(async (query) => {
        const names = await suggestSharedTags(query || '', 20);
        return (names || []).map((name) => ({name}));
    }, []);

    if (!enabled || !entityId) return null;

    return (
        <Stack spacing={0.75} sx={{width: '100%', minWidth: 0}}>
            <TagList
                tags={tags}
                createTag={canEdit ? createTag : null}
                voteTag={canEdit ? removeTag : null}
                isPosting={busy}
                loadingTagOps={loadingOps}
                showAdd={canEdit}
                searchTagsFn={searchTagsFn}
                onTagClick={(tag) => {
                    if (tag?.name) navigate(`/tags/${encodeURIComponent(tag.name)}`);
                }}
            />
            {canEdit && showSuggestions && (
                <SharedSuggestedTags
                    entityType={entityType}
                    entityId={entityId}
                    currentTags={tags}
                    onAdd={(name) => createTag(name)}
                    disabled={busy}
                    enabled={enabled}
                />
            )}
            {error && (
                <Typography variant="caption" color="error" sx={{display: 'block'}}>
                    {error}
                </Typography>
            )}
        </Stack>
    );
}
